import React from 'react';

import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import RuleIcon from '@mui/icons-material/Rule';
import TableChartIcon from '@mui/icons-material/TableChart';
import { Box, Button, Chip, Collapse, Divider, Paper, Stack, TextField, Typography } from '@mui/material';

export type ChecklistRuleDataPoint = {
  error?: boolean;
  label: string;
  readOnly?: boolean;
  value: string;
  onChange?: (value: string) => void;
};

interface Props {
  approved: boolean;
  dataPoints: Array<ChecklistRuleDataPoint>;
  expanded: boolean;
  manuallyApproved: boolean;
  rule: string;
  summary: string;
  title: string;
  approvalNote?: string;
  dataPointsLabel?: string;
  onApprovalChange?: () => void;
  showApprovalNote?: boolean;
  onToggleExpanded: () => void;
}

export default function ChecklistRuleCard({
  approved,
  dataPoints,
  expanded,
  manuallyApproved,
  rule,
  summary,
  title,
  approvalNote,
  dataPointsLabel = 'Data points',
  onApprovalChange,
  showApprovalNote = false,
  onToggleExpanded,
}: Props) {
  const approvalLabel = getApprovalLabel(approved, manuallyApproved);
  const showNote = showApprovalNote && !!approvalNote;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: approved ? 'success.light' : 'warning.light',
        borderRadius: 1.25,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
        sx={{
          bgcolor: approved ? 'success.50' : 'warning.50',
          px: 2.5,
          py: 2.25,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ color: approved ? 'success.main' : 'warning.main', display: 'flex', pt: 0.25 }}>
            {approved ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle1" fontWeight={700}>
                {title}
              </Typography>
              <Chip
                color={approved ? 'success' : 'warning'}
                icon={approved ? <CheckIcon /> : <ErrorOutlineIcon />}
                label={approvalLabel}
                size="small"
                variant="outlined"
                sx={{ bgcolor: 'background.paper', fontWeight: 700 }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {summary}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent={{ xs: 'flex-end', md: 'flex-start' }}>
          <Button
            color="inherit"
            size="small"
            variant="text"
            endIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            onClick={onToggleExpanded}
            sx={{ textTransform: 'none' }}
          >
            {expanded ? 'Hide details' : 'Show details'}
          </Button>
          {approved ? (
            <Button
              color="inherit"
              size="small"
              variant="text"
              startIcon={<KeyboardReturnIcon />}
              disabled
              sx={{ textTransform: 'none' }}
            >
              Require approval
            </Button>
          ) : (
            <Button
              color="inherit"
              size="small"
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={onApprovalChange}
              sx={{
                bgcolor: 'grey.900',
                color: 'common.white',
                textTransform: 'none',
                '&:hover': { bgcolor: 'grey.800' },
              }}
            >
              Approve
            </Button>
          )}
        </Stack>
      </Stack>

      <Collapse in={expanded}>
        <Stack spacing={2.25} sx={{ bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider', p: 2.5 }}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <RuleIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={700}>
                Rule
              </Typography>
            </Stack>
            <Paper variant="outlined" sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 1.5 }}>
              <Typography variant="body2">{rule}</Typography>
            </Paper>
          </Stack>

          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TableChartIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={700}>
                {dataPointsLabel}
              </Typography>
            </Stack>
            <Paper variant="outlined" sx={{ bgcolor: 'background.paper', borderRadius: 1, overflow: 'hidden' }}>
              {dataPoints.map((dataPoint, index) => (
                <Box key={dataPoint.label}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    sx={{ px: 2, py: 1.25 }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ flex: '0 0 240px', fontWeight: 500 }}>
                      {dataPoint.label}
                    </Typography>
                    <TextField
                      error={dataPoint.error}
                      onChange={(event) => dataPoint.onChange?.(event.target.value)}
                      size="small"
                      value={dataPoint.value}
                      InputProps={{
                        readOnly: dataPoint.readOnly ?? !dataPoint.onChange,
                        sx: dataPoint.error ? { color: 'error.main' } : undefined,
                      }}
                      inputProps={{
                        'aria-invalid': dataPoint.error,
                        'aria-readonly': dataPoint.readOnly ?? !dataPoint.onChange,
                      }}
                      fullWidth
                    />
                  </Stack>
                  {index < dataPoints.length - 1 && <Divider />}
                </Box>
              ))}
            </Paper>
          </Stack>

          {showNote && (
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PriorityHighIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight={700}>
                  Approval note
                </Typography>
              </Stack>
              <Paper variant="outlined" sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 1.5 }}>
                <Typography variant="body2">{approvalNote}</Typography>
              </Paper>
            </Stack>
          )}
        </Stack>
      </Collapse>
    </Paper>
  );
}

function getApprovalLabel(approved: boolean, manuallyApproved: boolean) {
  if (!approved) {
    return 'Requires approval';
  }

  return manuallyApproved ? 'Manually approved' : 'Approved';
}
